import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  Paper,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { sessionAPI, treatmentAPI, medicineAPI } from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 900,
  margin: '0 auto',
  padding: theme.spacing(2),
}));

const PatientInfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.grey[200]}`,
}));

function SessionForm() {
  const { treatmentId } = useParams();
  const navigate = useNavigate();
  const [treatment, setTreatment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [sessionNumber, setSessionNumber] = useState(1);

  const [formData, setFormData] = useState({
    treatmentId: treatmentId,
    sessionDate: dayjs(),
    performedBy: '',
    cost: '',
    notes: '',
    nextSessionDate: null,
    prescribedMedicines: []
  });

  const [medicineRow, setMedicineRow] = useState({
    medicineName: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: ''
  });

  useEffect(() => {
    fetchTreatmentDetails();
    fetchMedicines();
    fetchSessionNumber();
  }, [treatmentId]);

  const fetchTreatmentDetails = async () => {
    try {
      const response = await treatmentAPI.getById(treatmentId);
      setTreatment(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch treatment details');
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await medicineAPI.getAll({ limit: 1000 });
      setMedicines(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch medicines');
    }
  };

  const fetchSessionNumber = async () => {
    try {
      const response = await sessionAPI.getByTreatmentId(treatmentId);
      const sessions = response.data.data || [];
      setSessionNumber(sessions.length + 1);
    } catch (err) {
      console.error('Failed to fetch session count');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (newValue) => {
    setFormData({
      ...formData,
      sessionDate: newValue
    });
  };

  const handleNextSessionDateChange = (newValue) => {
    setFormData({
      ...formData,
      nextSessionDate: newValue
    });
  };

  const handleMedicineRowChange = (e) => {
    setMedicineRow({
      ...medicineRow,
      [e.target.name]: e.target.value
    });
  };

  const handleMedicineSelect = (medicine) => {
    setMedicineRow({
      ...medicineRow,
      medicineName: medicine.name,
      dosage: medicine.defaultDosage || medicineRow.dosage,
      frequency: medicine.commonFrequencies?.[0] || medicineRow.frequency
    });
  };

  const addMedicine = () => {
    if (!medicineRow.medicineName || !medicineRow.frequency) {
      setError('Please fill in medicine name and frequency');
      return;
    }

    setFormData({
      ...formData,
      prescribedMedicines: [...formData.prescribedMedicines, { ...medicineRow }]
    });

    setMedicineRow({
      medicineName: '',
      dosage: '',
      frequency: '',
      duration: '',
      instructions: ''
    });
    setError('');
  };

  const removeMedicine = (index) => {
    setFormData({
      ...formData,
      prescribedMedicines: formData.prescribedMedicines.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        sessionDate: formData.sessionDate?.toISOString(),
        nextSessionDate: formData.nextSessionDate?.toISOString() || null
      };
      const response = await sessionAPI.create(submitData);
      navigate(`/session/${response.data.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  if (!treatment) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const patient = treatment.patient;

  return (
    <Box sx={{ p: 3 }}>
      <StyledCard>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
            Add Session - Session #{sessionNumber}
          </Typography>

          <PatientInfoCard>
            <Typography variant="h6" gutterBottom>
              Patient & Treatment Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography><strong>Patient:</strong> {patient?.firstName} {patient?.lastName}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography><strong>Age:</strong> {patient?.dateOfBirth ? calculateAge(patient.dateOfBirth) : 'N/A'} years</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography><strong>Gender:</strong> {patient?.gender}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography><strong>Treatment:</strong> {treatment.treatmentType}</Typography>
              </Grid>
            </Grid>
          </PatientInfoCard>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Session Date & Time *"
                  value={formData.sessionDate}
                  onChange={handleDateChange}
                  sx={{ width: '100%' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Performed By *"
                  name="performedBy"
                  value={formData.performedBy}
                  onChange={handleChange}
                  placeholder="Name of doctor/staff"
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cost"
                  name="cost"
                  type="number"
                  slotProps={{
                    htmlInput: { step: '0.01', min: '0' }
                  }}
                  value={formData.cost}
                  onChange={handleChange}
                  placeholder="0.00"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  Prescribed Medicines
                </Typography>

                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={2}>
                      <Autocomplete
                        freeSolo
                        options={medicines.map(med => med.name)}
                        value={medicineRow.medicineName}
                        onChange={(event, newValue) => {
                          setMedicineRow({
                            ...medicineRow,
                            medicineName: newValue || ''
                          });
                          if (newValue) {
                            const selectedMedicine = medicines.find(med => med.name === newValue);
                            if (selectedMedicine) {
                              handleMedicineSelect(selectedMedicine);
                            }
                          }
                        }}
                        onInputChange={(event, newInputValue) => {
                          setMedicineRow({
                            ...medicineRow,
                            medicineName: newInputValue
                          });
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Medicine Name"
                            size="small"
                            placeholder="Enter or select medicine"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Dosage"
                        name="dosage"
                        value={medicineRow.dosage}
                        onChange={handleMedicineRowChange}
                        placeholder="e.g., 500mg"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Frequency"
                        name="frequency"
                        value={medicineRow.frequency}
                        onChange={handleMedicineRowChange}
                        placeholder="e.g., Twice daily"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Duration"
                        name="duration"
                        value={medicineRow.duration}
                        onChange={handleMedicineRowChange}
                        placeholder="e.g., 5 days"
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Instructions"
                        name="instructions"
                        value={medicineRow.instructions}
                        onChange={handleMedicineRowChange}
                        placeholder="Optional"
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={addMedicine}
                        startIcon={<AddIcon />}
                        sx={{ height: '40px' }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {formData.prescribedMedicines.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Medicine</TableCell>
                        <TableCell>Dosage</TableCell>
                        <TableCell>Frequency</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Instructions</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.prescribedMedicines.map((med, index) => (
                        <TableRow key={index}>
                          <TableCell>{med.medicineName}</TableCell>
                          <TableCell>{med.dosage}</TableCell>
                          <TableCell>{med.frequency}</TableCell>
                          <TableCell>{med.duration || '-'}</TableCell>
                          <TableCell>{med.instructions || '-'}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeMedicine(index)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Session Notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Any observations or notes from this session..."
                />
              </Grid>

              <Grid item xs={12}>
                <DateTimePicker
                  label="Next Session Date (Optional)"
                  value={formData.nextSessionDate}
                  onChange={handleNextSessionDateChange}
                  sx={{ width: '100%' }}
                  slotProps={{
                    textField: {
                      helperText: 'Schedule the next session if needed'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/treatment/${treatmentId}`)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={16} />}
                  >
                    {loading ? 'Creating...' : 'Create Session'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </StyledCard>
    </Box>
  );
}

export default SessionForm;
